import { dashboard } from 'types';
import { payloadToJSON, toJSON, toJSONArray } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';

export const initialState: State = {
  menuCategories: {
    list: [],
    requestInProgress: false,
    isInitialized: false,
  },
  menus: {
    list: [],
    requestInProgress: false,
    isInitialized: false,
  },
  menuSections: {
    requestInProgress: false,
    isInitialized: false,
  },
  menuItems: {
    list: [],
    requestInProgress: false,
    isInitialized: false,
  },
  menuModifierGroups: {
    list: [],
    requestInProgress: false,
    isInitialized: false,
  },
  menuModifierGroupsToItems: {
    list: [],
    requestInProgress: false,
    isInitialized: false,
  },
};

export interface RequestableList<T> {
  list: T[];
  requestInProgress: boolean;
  isInitialized: boolean;
}

export interface State {
  menuCategories: RequestableList<dashboard.MenuCategory>;
  menus: RequestableList<dashboard.Menu>;
  menuSections: {
    requestInProgress: boolean;
    isInitialized: boolean;
  };
  menuItems: RequestableList<dashboard.MenuItem>;
  menuModifierGroups: RequestableList<dashboard.MenuModifierGroup>;
  menuModifierGroupsToItems: RequestableList<dashboard.MenuModifierGroupToMenuItem>;
}

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;

  switch (type) {
    case AppConstants.MENU_CATEGORY_FETCH_DONE: {
      return {
        ...state,
        menuCategories: {
          ...state.menuCategories,
          requestInProgress: false,
          isInitialized: true,
          list: toJSONArray<dashboard.MenuCategory>(payload),
        },
      };
    }
    case AppConstants.MENU_CATEGORY_INSERT_STARTING:
    case AppConstants.MENU_CATEGORY_UPDATE_STARTING:
    case AppConstants.MENU_CATEGORY_FETCH_STARTING: {
      return {
        ...state,
        menuCategories: {
          ...state.menuCategories,
          requestInProgress: true,
        },
      };
    }
    case AppConstants.MENU_CATEGORY_INSERT_DONE:
    case AppConstants.MENU_CATEGORY_UPDATE_DONE:
    case AppConstants.MENU_CATEGORY_INSERT_FAILED:
    case AppConstants.MENU_CATEGORY_UPDATE_FAILED:
    case AppConstants.MENU_CATEGORY_FETCH_FAILED: {
      return {
        ...state,
        menuCategories: {
          ...state.menuCategories,
          requestInProgress: false,
        },
      };
    }

    case AppConstants.MENU_FETCH_DONE: {
      return {
        ...state,
        menus: {
          ...state.menus,
          requestInProgress: false,
          isInitialized: true,
          list: toJSONArray<dashboard.Menu>(payload),
        },
      };
    }
    case AppConstants.MENU_INSERT_STARTING:
    case AppConstants.MENU_UPDATE_STARTING:
    case AppConstants.MENU_COPY_STARTING:
    case AppConstants.MENU_FETCH_STARTING: {
      return {
        ...state,
        menus: {
          ...state.menus,
          requestInProgress: true,
        },
      };
    }
    case AppConstants.MENU_COPY_DONE:
    case AppConstants.MENU_INSERT_DONE: {
      const newMenu = payloadToJSON<dashboard.Menu>(payload);
      return {
        ...state,
        menus: {
          ...state.menus,
          list: [...state.menus.list, newMenu],
          requestInProgress: false,
        },
      };
    }
    case AppConstants.MENU_COPY_FAILED:
    case AppConstants.MENU_INSERT_FAILED:
    case AppConstants.MENU_UPDATE_FAILED:
    case AppConstants.MENU_FETCH_FAILED: {
      return {
        ...state,
        menus: {
          ...state.menus,
          requestInProgress: false,
        },
      };
    }

    case AppConstants.MENU_UPDATE_DONE: {
      const updatedMenu = payloadToJSON<dashboard.Menu>(payload);
      return {
        ...state,
        menus: {
          ...state.menus,
          list: state.menus.list.map((menu) => (menu.id === updatedMenu.id ? updatedMenu : menu)),
          requestInProgress: false,
        },
      };
    }

    case AppConstants.MENU_SECTION_INSERT_DONE: {
      const newSection = payloadToJSON<dashboard.MenuSection>(payload);
      return {
        ...state,
        menus: {
          ...state.menus,
          list: state.menus.list.map((menu) =>
            menu.id === newSection.menuId
              ? { ...menu, menuSection: [...menu.menuSection, newSection] }
              : menu
          ),
          requestInProgress: false,
        },
        menuSections: {
          ...state.menuSections,
          requestInProgress: false,
        },
      };
    }

    case AppConstants.MENU_SECTION_UPDATE_DONE: {
      const updatedSection = payloadToJSON<dashboard.MenuSection>(payload);

      return {
        ...state,
        menus: {
          ...state.menus,
          list: state.menus.list.map((menu) =>
            menu.id === updatedSection.menuId
              ? {
                  ...menu,
                  menuSection: updatedSection.isArchived
                    ? menu.menuSection.filter((section) => updatedSection.id !== section.id)
                    : menu.menuSection.map((section) =>
                        updatedSection.id === section.id ? updatedSection : section
                      ),
                }
              : menu
          ),
          requestInProgress: false,
        },
        menuSections: {
          ...state.menuSections,
          requestInProgress: false,
        },
      };
    }

    case AppConstants.MENU_SECTION_INSERT_STARTING:
    case AppConstants.MENU_SECTION_UPDATE_STARTING: {
      return {
        ...state,
        menuSections: {
          ...state.menuSections,
          requestInProgress: true,
        },
      };
    }
    case AppConstants.MENU_SECTION_INSERT_FAILED:
    case AppConstants.MENU_SECTION_UPDATE_FAILED: {
      return {
        ...state,
        menuSections: {
          ...state.menuSections,
          requestInProgress: false,
        },
      };
    }

    case AppConstants.MENU_ITEM_FETCH_DONE: {
      return {
        ...state,
        menuItems: {
          ...state.menuItems,
          requestInProgress: false,
          isInitialized: true,
          list: toJSONArray<dashboard.MenuItem>(payload),
        },
      };
    }
    case AppConstants.MENU_ITEM_INSERT_STARTING:
    case AppConstants.MENU_ITEM_UPDATE_STARTING:
    case AppConstants.MENU_ITEM_FETCH_STARTING: {
      return {
        ...state,
        menuItems: {
          ...state.menuItems,
          requestInProgress: true,
        },
      };
    }
    case AppConstants.MENU_ITEM_INSERT_DONE: {
      const insertedMenuItem = payloadToJSON<dashboard.MenuItem>(payload);
      return {
        ...state,
        menuItems: {
          ...state.menuItems,
          list: state.menuItems.list.concat([insertedMenuItem]),
          requestInProgress: false,
        },
      };
    }
    case AppConstants.MENU_ITEM_UPDATE_DONE: {
      const updatedMenuItem = payloadToJSON<dashboard.MenuItem>(payload);
      return {
        ...state,
        menus: {
          ...state.menus,
          list: state.menus.list.map((menu) => ({
            ...menu,
            menuSection: menu.menuSection.map((menuSection) => ({
              ...menuSection,
              menuItem: menuSection.menuItem?.map((menuItem) =>
                updatedMenuItem.id === menuItem.id ? updatedMenuItem : menuItem
              ),
            })),
          })),
        },
        menuItems: {
          ...state.menuItems,
          list: state.menuItems.list.map((menuItem) =>
            updatedMenuItem.id === menuItem.id ? updatedMenuItem : menuItem
          ),
          requestInProgress: false,
        },
      };
    }
    case AppConstants.MENU_ITEM_INSERT_FAILED:
    case AppConstants.MENU_ITEM_UPDATE_FAILED:
    case AppConstants.MENU_ITEM_FETCH_FAILED: {
      return {
        ...state,
        menuItems: {
          ...state.menuItems,
          requestInProgress: false,
        },
      };
    }

    case AppConstants.MENU_MODIFIER_GROUP_FETCH_DONE: {
      return {
        ...state,
        menuModifierGroups: {
          ...state.menuModifierGroups,
          requestInProgress: false,
          isInitialized: true,
          list: toJSONArray<dashboard.MenuModifierGroup>(payload),
        },
      };
    }
    case AppConstants.MENU_MODIFIER_GROUP_INSERT_STARTING:
    case AppConstants.MENU_MODIFIER_GROUP_UPDATE_STARTING:
    case AppConstants.MENU_MODIFIER_GROUP_FETCH_STARTING: {
      return {
        ...state,
        menuModifierGroups: {
          ...state.menuModifierGroups,
          requestInProgress: true,
        },
      };
    }
    case AppConstants.MENU_MODIFIER_GROUP_INSERT_DONE: {
      const newModifierGroup = toJSON<dashboard.MenuModifierGroup>(payload);
      return {
        ...state,
        menuModifierGroups: {
          ...state.menuModifierGroups,
          requestInProgress: false,
          list: [...state.menuModifierGroups.list, newModifierGroup],
        },
      };
    }
    case AppConstants.MENU_MODIFIER_GROUP_UPDATE_DONE: {
      const newModifierGroup = toJSON<dashboard.MenuModifierGroup>(payload);
      return {
        ...state,
        menuModifierGroups: {
          ...state.menuModifierGroups,
          requestInProgress: false,
          list: state.menuModifierGroups.list.map((modifierGroup) =>
            modifierGroup.id === newModifierGroup.id ? newModifierGroup : modifierGroup
          ),
        },
      };
    }
    case AppConstants.MENU_MODIFIER_GROUP_INSERT_FAILED:
    case AppConstants.MENU_MODIFIER_GROUP_UPDATE_FAILED:
    case AppConstants.MENU_MODIFIER_GROUP_FETCH_FAILED: {
      return {
        ...state,
        menuModifierGroups: {
          ...state.menuModifierGroups,
          requestInProgress: false,
        },
      };
    }

    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_FETCH_DONE: {
      return {
        ...state,
        menuModifierGroupsToItems: {
          ...state.menuModifierGroupsToItems,
          requestInProgress: false,
          isInitialized: true,
          list: toJSONArray<dashboard.MenuModifierGroupToMenuItem>(payload),
        },
      };
    }
    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_INSERT_STARTING:
    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_FETCH_STARTING:
    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_DELETE_STARTING: {
      return {
        ...state,
        menuModifierGroupsToItems: {
          ...state.menuModifierGroupsToItems,
          requestInProgress: true,
        },
      };
    }
    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_INSERT_DONE:
    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_DELETE_DONE:
    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_INSERT_FAILED:
    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_FETCH_FAILED:
    case AppConstants.MENU_MODIFIER_GROUP_TO_ITEM_DELETE_FAILED: {
      return {
        ...state,
        menuModifierGroupsToItems: {
          ...state.menuModifierGroupsToItems,
          requestInProgress: false,
        },
      };
    }
    case AppConstants.MENU_ITEM_MODIFIER_GROUPS_UPDATE_DONE: {
      const menuItemId: number | undefined = context?.menuItemId;

      if (!menuItemId) {
        return state;
      }

      const updatedMenuItemModifierGroups =
        toJSONArray<dashboard.MenuModifierGroupToMenuItem>(payload);

      const updatedAllModifierGroupToMenuItems = [
        ...state.menuModifierGroupsToItems.list.filter(
          (modifierGroupToMenuItem) => modifierGroupToMenuItem.menuItemId !== menuItemId
        ),
        ...updatedMenuItemModifierGroups,
      ];

      return {
        ...state,
        menuModifierGroupsToItems: {
          ...state.menuModifierGroupsToItems,
          list: updatedAllModifierGroupToMenuItems,
        },
      };
    }

    default:
      return state;
  }
}
